import { useEffect, useMemo, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { MonitoringComponent } from "@/routes/processes/[processId]/qpm/requirements/[requirementId]/assessment/_components/monitoring-component";
import { RiskAnalysisComponent } from "@/routes/processes/[processId]/qpm/requirements/[requirementId]/assessment/_components/risk-analysis-component";
import {
  MonitorIoTClass,
  MonitorType,
  QPMSearchParams,
} from "@/services/backend/qpm/types/enums";
import { useSearchParams } from "react-router";
import type { RequirementArea } from "@/services/backend/qpm/requirements/types/requirement-area";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";
import { accumulateDerivedIoTDependents } from "@/services/backend/qpm/requirements/lib/derived-iot-dependents";

export type MonitorAreaSegmentProps = {
  requirement: RequirementDTO;
  index: number;
  area: RequirementArea;
  buildingName: string;
  levelName: string;
};

export function MonitoringAreaSegmentCollapsible({
  requirement,
  index,
  area,
  buildingName,
  levelName,
}: MonitorAreaSegmentProps) {
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState<boolean>(true);

  useEffect(() => {
    const collapsibleOpen = searchParams.get(
      QPMSearchParams.CollapsibleAreasOpen,
    );
    if (collapsibleOpen && collapsibleOpen === "false") {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [searchParams]);

  const truncateString = (input: string): string => {
    if (input.length <= 12) {
      return input;
    }
    return `${input.slice(0, 5)}...${input.slice(-5)}`;
  };

  const monitorSummary = useMemo(() => {
    const typeSummaryItems: {
      monitorType: MonitorType;
      iotClass?: MonitorIoTClass;
      amount: number;
    }[] = [];

    const inc = (
      monitorType: MonitorType,
      amount: number,
      iotClass?: MonitorIoTClass,
    ) => {
      const idx = typeSummaryItems.findIndex(
        (x) => x.monitorType === monitorType && x.iotClass === iotClass,
      );
      if (idx >= 0) {
        typeSummaryItems[idx].amount += amount;
        return;
      }
      typeSummaryItems.push({ monitorType, iotClass, amount });
    };

    let requiredSimAmount = 0;
    let requiredPortalAmount = 0;

    // Count selected monitors for this area
    requirement.selectedMonitors.forEach((monitor) => {
      if (monitor.areaId !== area.areaId) return;

      inc(monitor.monitorTypeKey, monitor.amount);

      if (monitor.monitorTypeKey === MonitorType.IoT) {
        const snap = requirement.monitorSnaps?.[monitor.monitorId];
        const iotClass =
          monitor.ioTSpec?.class ?? snap?.capabilities?.iot?.class;
        if (iotClass) {
          inc(MonitorType.IoT, monitor.amount, iotClass);

          const next = accumulateDerivedIoTDependents(
            {
              requiredSimAmount,
              requiredPortalAmount,
            },
            iotClass,
            monitor.amount,
          );
          requiredSimAmount = next.requiredSimAmount;
          requiredPortalAmount = next.requiredPortalAmount;
        }
      }
    });

    // IoT derived dependents:
    // - SIM: one per gateway and one per IoTPestMonitorWithSim
    // - PORTAL: one per endpoint (sensor + pest monitor)
    if (requiredSimAmount > 0) {
      // inc(MonitorType.IoT, requiredSimAmount);
      inc(MonitorType.IoT, requiredSimAmount, MonitorIoTClass.IoTSim);
    }
    if (requiredPortalAmount > 0) {
      // inc(MonitorType.IoT, requiredPortalAmount);
      inc(MonitorType.IoT, requiredPortalAmount, MonitorIoTClass.IoTPortal);
    }

    const getAmount = (monitorType: MonitorType, cls?: MonitorIoTClass) =>
      typeSummaryItems.find(
        (x) => x.monitorType === monitorType && x.iotClass === cls,
      )?.amount ?? 0;

    return {
      typeSummaryItems,
      getAmount,
    };
  }, [area.areaId, requirement.monitorSnaps, requirement.selectedMonitors]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="overflow-auto-x">
        <div className="bg-muted/50 flex w-full flex-col justify-between md:flex-row">
          <div className="flex w-full items-center justify-between pe-2">
            <CollapsibleTrigger>
              <div className="flex items-center text-sm font-bold uppercase">
                <div className="py-3 pl-6">
                  {open ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                </div>
                <div className="grow py-3 pr-6 pl-3">
                  {truncateString(buildingName)} - {truncateString(levelName)} -{" "}
                  {area.area.base.name}
                </div>
              </div>
            </CollapsibleTrigger>
            <RiskAnalysisComponent reqArea={area} />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="p-1 font-bold">
                {MonitorTypeUtil.toString(MonitorType.Checkpoint)}:
              </span>
              <span className="m-1 min-w-[50px] bg-white p-1 text-end">
                {monitorSummary.getAmount(MonitorType.Checkpoint) || 0}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="p-1 font-bold">
                {MonitorTypeUtil.toString(MonitorType.FlyExterminator)}:
              </span>
              <span className="m-1 min-w-[50px] bg-white p-1 text-end">
                {monitorSummary.getAmount(MonitorType.FlyExterminator) || 0}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="p-1 font-bold">
                {MonitorTypeUtil.toString(MonitorType.IoT)}:
              </span>
              <span className="m-1 min-w-[50px] bg-white p-1 text-end">
                {monitorSummary.getAmount(MonitorType.IoT) || 0}
              </span>
            </div>
          </div>
        </div>
        <CollapsibleContent>
          <MonitoringComponent
            requirement={requirement}
            areaIndex={index}
            areaId={area.area.base.id}
          />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
